import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Truck, Camera, Phone, Save, ShieldCheck, Lock, Settings as SettingsIcon, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { API, api } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useT } from "@/lib/i18n";

/**
 * Settings page (Tab 6) — Delivery providers (Wolt / Foody / Bolt) +
 * Live Camera RTSP/HTTPS URLs per kiosk + Owner WhatsApp number.
 *
 * Gated by the legacy MASTER owner credentials (Bearer savvas:savvas).
 * After a successful sign-in the credentials live for the session.
 */

const OWNER_KEY = "kioskforce.owner_bearer";

const emptyDelivery = { api_key: "", webhook_url: "", enabled: false };

const DEFAULTS = {
  owner_whatsapp_phone: "",
  wolt:  { ...emptyDelivery },
  foody: { ...emptyDelivery },
  bolt:  { ...emptyDelivery },
  cameras: {
    vizantiou_url: "", stavrou_url: "", kallipoleos_url: "", aglantzia_url: "",
  },
};

export default function Settings() {
  const t = useT();
  const nav = useNavigate();
  const [bearer, setBearer] = useState(() => localStorage.getItem(OWNER_KEY) || "");
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  const [data, setData] = useState(DEFAULTS);
  const [kiosks, setKiosks] = useState([]); // per-tenant list (dynamic)
  const [plan, setPlan] = useState("master");
  const [maxDelivery, setMaxDelivery] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  // Iter 37 — HARD RESET (self-serve sandbox wipe) state.
  // Iter 44 — Tokenless dual-auth: we hit the endpoint with an explicit
  // `X-AppLock-Auth: savvaspos:store` header (the same kiosk password
  // the operator just typed at AppLock). The backend accepts EITHER a
  // Bearer JWT OR this header, so the "Missing bearer token" overlay
  // is structurally impossible — localStorage is never read.
  const [hardResetOpen, setHardResetOpen] = useState(false);
  const [hardResetBusy, setHardResetBusy] = useState(false);
  const runHardReset = async () => {
    setHardResetBusy(true);
    try {
      // Use raw fetch to bypass the Axios interceptor entirely so no
      // Authorization header leaks in. The AppLock device password
      // proves master-tenant possession server-side.
      const resp = await fetch(`${API}/admin/reset-sandbox`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-AppLock-Auth": "savvaspos:store",
        },
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      toast.success(
        `Reset complete · ${data.total} rows wiped · ${
          Object.values(data.rebuilt || {}).filter((v) => v === "dropped").length
        } collections dropped`
      );
      setHardResetOpen(false);
      // Force global sign-out: wipe local kiosk state and bounce to
      // AppLock so the operator re-authenticates on a clean slate.
      setTimeout(() => {
        try { localStorage.clear(); } catch { /* ignore */ }
        window.location.assign("/");
      }, 800);
    } catch (e) {
      toast.error(e.message || "Hard reset failed");
    } finally {
      setHardResetBusy(false);
    }
  };

  // Iter 100/105 — Milestone Database Purge. Hits POST /api/v1/owner/purge-test-data
  // to drop legacy shifts with null total_sales AND KRONOS €0.00 seed
  // rows. On success we clear the local caches + refresh the router so
  // both the right-hand CONTENTS panel and the KRONOS Audit History
  // matrix land in a clean, production-ready state.
  //
  // Iter 105 hot-fix — dynamically extract the bearer token from
  // localStorage at the millisecond of the click (both `kf_jwt`, our
  // canonical Iter 64 key, AND `token`, the legacy alias) and inject
  // it explicitly into the axios call. This bypasses any stale
  // interceptor state that could otherwise raise "Missing bearer
  // token" on the round-trip.
  const [purgeBusy, setPurgeBusy] = useState(false);
  const runIter100Purge = async () => {
    if (purgeBusy) return;
    if (!window.confirm(
      "🚨 Πρόκειται να διαγράψετε ΟΛΑ τα legacy δοκιμαστικά δεδομένα " +
      "(shifts χωρίς total_sales + KRONOS €0.00 audit rows). Είστε βέβαιοι;\n\n" +
      "This will permanently delete legacy test shifts + KRONOS €0.00 audit rows. Continue?"
    )) return;
    // Iter 105 — resolve the active bearer token from every candidate
    // localStorage slot (Iter 64 primary + legacy aliases) so the
    // click never fires against a stale interceptor.
    let liveToken = "";
    try {
      liveToken =
        localStorage.getItem("kf_jwt") ||
        localStorage.getItem("token") ||
        localStorage.getItem("access_token") ||
        "";
    } catch { /* localStorage denied — liveToken stays "" */ }
    if (!liveToken) {
      toast.error(
        "🔒 Missing bearer token · παρακαλώ κάντε ξανά login πριν από τον καθαρισμό",
        { id: "iter105-purge-no-token" }
      );
      return;
    }
    setPurgeBusy(true);
    try {
      const { data } = await api.post(
        "/v1/owner/purge-test-data",
        {},
        {
          headers: { Authorization: `Bearer ${liveToken}` },
        }
      );
      if (!data?.ok) throw new Error("purge did not confirm ok");
      const { shifts_null_total_sales = 0, kronos_audits_zero_or_seed = 0 } =
        data.purged || {};
      toast.success(
        `🧹 Purge complete · ${shifts_null_total_sales} shifts + ${kronos_audits_zero_or_seed} KRONOS rows wiped`,
        { id: "iter100-purge-ok", duration: 5000 }
      );
      // Iter 100 spec — refresh the router so the right-hand CONTENTS
      // + KRONOS Audit History matrix reload against the freshly-clean DB.
      setTimeout(() => window.location.reload(), 900);
    } catch (err) {
      const detail =
        err?.response?.data?.detail || err?.message || "Purge failed";
      toast.error(`🧹 Purge failed · ${detail}`);
    } finally {
      setPurgeBusy(false);
    }
  };

  const authHeader = bearer ? { Authorization: `Bearer ${bearer}` } : {};

  const load = async () => {
    if (!bearer) return;
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        fetch(`${API}/owner/settings`, { headers: authHeader }),
        fetch(`${API}/owner/kiosks`, { headers: authHeader }),
      ]);
      if (r1.status === 403 || r2.status === 403) {
        localStorage.removeItem(OWNER_KEY);
        setBearer("");
        return;
      }
      const j1 = await r1.json();
      const j2 = await r2.json();
      setData({ ...DEFAULTS, ...j1 });
      setKiosks(j2.kiosks || []);
      setPlan(j2.plan || "master");
      setMaxDelivery(j2.max_delivery_toggles);
    } catch (e) {
      toast.error(`Load failed: ${e?.message || e}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bearer]);

  const signIn = (e) => {
    e?.preventDefault?.();
    const token = `${u.trim().toLowerCase()}:${p}`;
    localStorage.setItem(OWNER_KEY, token);
    setBearer(token);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/owner/settings`, {
        method: "PUT",
        headers: { ...authHeader, "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const j = await r.json();
      setData({ ...DEFAULTS, ...j });
      toast.success("Settings saved · Οι ρυθμίσεις αποθηκεύτηκαν");
      // Iter 41 — Auto-fire a Twilio verification ping to the saved
      // owner phone so the operator instantly knows whether WhatsApp is
      // wired correctly. Fail-soft: never block the Save flow.
      if (j?.owner_whatsapp_phone) {
        try {
          const { data: t } = await api.post("/admin/whatsapp-test", {
            phone: j.owner_whatsapp_phone,
          });
          if (t?.ok) {
            toast.success(`📲 Test sent · SID ${(t.sid || "").slice(0, 12)}…`);
          } else {
            toast.error(
              `Test failed: ${t?.reason || "unknown"}${t?.detail ? ` — ${t.detail}` : ""}`,
              { duration: 8000 },
            );
          }
        } catch (eTest) {
          toast.error(
            `Test fire crashed: ${eTest.response?.data?.detail || eTest.message}`,
            { duration: 8000 },
          );
        }
      }
    } catch (e) {
      toast.error(`Save failed: ${e?.message || e}`);
    } finally {
      setSaving(false);
    }
  };

  const setDelivery = (key, field, value) => {
    setData((d) => {
      const next = { ...d, [key]: { ...d[key], [field]: value } };
      // SaaS tier client-side cap: toggling one provider on auto-disables
      // the other two so the UI mirrors the backend's
      // _enforce_single_delivery_for_saas rule.
      if (field === "enabled" && value && plan === "saas") {
        for (const other of ["wolt", "foody", "bolt"]) {
          if (other !== key) next[other] = { ...next[other], enabled: false };
        }
      }
      return next;
    });
  };

  const setKioskCam = (kioskName, url) =>
    setData((d) => ({
      ...d,
      cameras_by_kiosk: { ...(d.cameras_by_kiosk || {}), [kioskName]: url },
    }));

  // ── Owner-creds gate ────────────────────────────────────────────────
  if (!bearer) {
    return (
      <div className="max-w-md mx-auto py-12" data-testid="settings-locked-gate">
        <Card className="p-8">
          <div className="flex flex-col items-center mb-6">
            <div className="h-14 w-14 rounded-2xl bg-slate-900 text-white flex items-center justify-center mb-3">
              <Lock className="h-7 w-7" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight">Owner Access · Πρόσβαση Ιδιοκτήτη</h1>
            <p className="text-sm text-slate-500 mt-1">Settings are owner-gated.</p>
          </div>
          <form onSubmit={signIn} className="space-y-3">
            <Input
              placeholder="Owner username"
              value={u}
              onChange={(e) => setU(e.target.value)}
              data-testid="settings-owner-username"
            />
            <Input
              type="password"
              placeholder="Owner password"
              value={p}
              onChange={(e) => setP(e.target.value)}
              data-testid="settings-owner-password"
            />
            <Button type="submit" className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-bold" data-testid="settings-owner-submit">
              Unlock Settings
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  return (
    <div className="pb-12 space-y-6" data-testid="settings-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
            <SettingsIcon className="h-9 w-9" />
            {t("nav.settings")}
          </h1>
          <p className="text-slate-500 mt-1 tracking-wide">
            SETTINGS Folder MAIN · Delivery providers, live cameras, owner notifications.
          </p>
        </div>
        <Button
          onClick={save}
          disabled={saving || loading}
          className="bg-slate-900 hover:bg-slate-800 text-white font-bold h-11 px-6 gap-2"
          data-testid="settings-save"
        >
          <Save className="h-4 w-4" />
          {saving ? "Saving…" : "Save Settings"}
        </Button>
      </header>

      {/* Owner SMS */}
      <Card className="p-6" data-testid="settings-whatsapp-card">
        <div className="flex items-center gap-2 mb-4">
          <Phone className="h-5 w-5 text-emerald-600" />
          <h2 className="text-lg font-bold tracking-tight">
            📞 Τηλέφωνο Ειδοποιήσεων SMS (Owner SMS Notification Phone)
          </h2>
        </div>
        <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
          E.164 format · e.g. +357XXXXXXXX
        </Label>
        <Input
          value={data.owner_whatsapp_phone || ""}
          onChange={(e) => setData((d) => ({ ...d, owner_whatsapp_phone: e.target.value }))}
          placeholder="+35799123456"
          data-testid="settings-owner-whatsapp-phone"
          className="mt-1 font-mono"
        />
        <p className="text-[11px] text-slate-500 mt-2">
          Instantly receives an SMS summary (Cash, Cards, Deliveries, Discrepancy) the moment SAVE EVERYTHING & CLOSE is tapped. Z-Report photo + cashflow receipts are linked from the SMS as a single owner portal URL.
        </p>
      </Card>

      {/* Delivery providers */}
      <Card className="p-6" data-testid="settings-delivery-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-orange-600" />
            <h2 className="text-lg font-bold tracking-tight">Delivery Integrations · Παράδοση</h2>
          </div>
          {plan === "saas" && (
            <span
              className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full bg-amber-100 text-amber-800"
              data-testid="settings-saas-cap-badge"
            >
              €8 plan · max {maxDelivery || 1} active toggle
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { key: "wolt",  label: "Wolt Integration Settings",  hookPath: "/api/v1/wolt-webhook"  },
            { key: "foody", label: "Foody",                       hookPath: "/api/v1/foody-webhook" },
            { key: "bolt",  label: "Bolt",                        hookPath: "/api/v1/bolt-webhook"  },
          ].map(({ key, label, hookPath }) => (
            <div
              key={key}
              className="border border-slate-200 rounded-xl p-4 bg-slate-50/50"
              data-testid={`settings-delivery-${key}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold text-slate-900 text-sm">/{label}/</div>
                <Switch
                  checked={!!data[key]?.enabled}
                  onCheckedChange={(v) => setDelivery(key, "enabled", v)}
                  data-testid={`settings-delivery-${key}-toggle`}
                />
              </div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">API Key</Label>
              <Input
                type="password"
                value={data[key]?.api_key || ""}
                onChange={(e) => setDelivery(key, "api_key", e.target.value)}
                placeholder={`${label} API key`}
                data-testid={`settings-delivery-${key}-key`}
                className="mt-1 mb-3 font-mono text-xs"
              />
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Webhook URL</Label>
              <Input
                value={data[key]?.webhook_url || hookPath}
                onChange={(e) => setDelivery(key, "webhook_url", e.target.value)}
                placeholder={hookPath}
                data-testid={`settings-delivery-${key}-webhook`}
                className="mt-1 font-mono text-xs"
              />
              <p className="text-[10px] text-slate-400 mt-1">
                Point {label} at <code className="text-[10px]">{hookPath}</code> with the API Key above as <code className="text-[10px]">X-{key.charAt(0).toUpperCase() + key.slice(1)}-Api-Key</code>.
              </p>
            </div>
          ))}
        </div>
      </Card>

      {/* Live cameras — one input per tenant kiosk (dynamic) */}
      <Card className="p-6" data-testid="settings-cameras-card">
        <div className="flex items-center gap-2 mb-4">
          <Camera className="h-5 w-5 text-rose-600" />
          <h2 className="text-lg font-bold tracking-tight">
            /LIVE CAMERA STREAMS (Subfolders)/
          </h2>
        </div>

        {kiosks.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500" data-testid="settings-cameras-empty">
            No kiosks configured yet. Complete the onboarding wizard first to add your kiosks.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {kiosks.map((kiosk) => {
              const key = kiosk;
              // Backward-compat: if a MASTER legacy field exists, prefer it.
              const legacyKey = `${kiosk.toLowerCase()}_url`;
              const value =
                (data.cameras_by_kiosk && data.cameras_by_kiosk[key]) ||
                (data.cameras && data.cameras[legacyKey]) || "";
              return (
                <div
                  key={kiosk}
                  className="border border-slate-200 rounded-xl p-4 bg-slate-50/40"
                  data-testid={`settings-kiosk-folder-${kiosk}`}
                >
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-1">
                    /{kiosk}/
                  </div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                    Stream URL
                  </Label>
                  <Input
                    value={value}
                    onChange={(e) => setKioskCam(kiosk, e.target.value)}
                    placeholder="rtsp://… or https://stream…"
                    data-testid={`settings-cam-${kiosk}`}
                    className="mt-1 font-mono text-xs"
                  />
                </div>
              );
            })}
          </div>
        )}
        <Separator className="my-4" />
        <p className="text-[11px] text-slate-500">
          One stream URL per kiosk. Feeds the LIVE VIEW tab inside Private Portal. Supports RTSP / HLS / MJPEG. SaaS tenants only see their own kiosks — fully isolated from the master MASTER_JOHN workspace.
        </p>
      </Card>

      <div className="text-[11px] text-slate-400 flex items-center gap-1">
        <ShieldCheck className="h-3 w-3" />
        Last updated: {data.updated_at ? new Date(data.updated_at).toLocaleString() : "never"}
      </div>

      {/* Iter 78 — EDI IMAP MAILBOX CREDENTIALS */}
      <EdiImapSettingsCard />

      {/* Iter 79 — JCC / Viva Wallet API Connector */}
      <JccVivaSettingsCard />

      {/* Iter 76 — System Updates archive + per-tenant feature flags */}
      <SystemUpdatesCard />

      {/* Iter 37 — HARD RESET self-serve nuke (tenant-scoped) */}
      <Card
        className="p-6 border-2 border-red-300 bg-red-50/40"
        data-testid="settings-hard-reset-card"
      >
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="h-5 w-5 text-red-700" />
          <h2 className="text-lg font-bold tracking-tight text-red-900">
            ⚠️ ΟΛΙΚΗ ΔΙΑΓΡΑΦΗ / HARD RESET
          </h2>
        </div>
        <p className="text-sm text-red-900/80 leading-relaxed mb-4">
          Σβήνει ΟΛΑ τα τρανσακτικά δεδομένα του δικού σου χώρου (τιμολόγια,
          επιταγές, cashflow, Z-Reports, TFA Form 4). Δεν αγγίζει τις ρυθμίσεις
          ή το λογαριασμό σου. Wipes ALL transactional data scoped to your
          tenant_id. Settings + account remain intact.
        </p>
        <Button
          onClick={() => setHardResetOpen(true)}
          className="bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-widest"
          data-testid="settings-hard-reset-btn"
        >
          ⚠️ ΟΛΙΚΗ ΔΙΑΓΡΑΦΗ / HARD RESET
        </Button>
      </Card>

      {/* ── Iter 100 · Milestone Database Purge Utility ────────────────── */}
      <Card
        className="p-5 border-2 border-rose-500 bg-gradient-to-br from-rose-50 via-white to-amber-50 shadow-lg"
        data-testid="iter100-purge-card"
      >
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="h-5 w-5 text-rose-600 animate-pulse" />
          <h3
            className="text-base font-black uppercase tracking-widest text-rose-800"
            data-testid="iter100-purge-title"
          >
            🚨 ΔΙΑΧΕΙΡΙΣΗ ΒΑΣΗΣ ΔΕΔΟΜΕΝΩΝ · DATABASE UTILITIES
          </h3>
        </div>
        <p className="text-[13px] leading-relaxed text-slate-700 mb-3">
          <strong>Iter 100 · Milestone Purge.</strong> Wipes legacy shifts
          missing <code className="font-mono text-rose-700">total_sales</code>{" "}
          and KRONOS audit rows with{" "}
          <code className="font-mono text-rose-700">€0.00 benchmarks</code>{" "}
          (e.g. 2026-06-26 seed data). Multi-tenant guardrail: strictly scoped
          to <code className="font-mono">MASTER_JOHN</code>. Non-destructive on
          real production rows.
          <br />
          <span className="text-slate-500 text-xs">
            Καθαρίζει legacy shifts χωρίς total_sales και KRONOS εγγραφές
            με €0.00 · ασφαλής για τα κανονικά production δεδομένα.
          </span>
        </p>
        <Button
          onClick={runIter100Purge}
          disabled={purgeBusy}
          className="bg-rose-600 hover:bg-rose-700 disabled:opacity-60 text-white font-bold uppercase tracking-widest gap-2"
          data-testid="iter100-purge-btn"
        >
          {purgeBusy ? "…" : "🧹 ΚΑΘΑΡΙΣΜΟΣ ΔΟΚΙΜΑΣΤΙΚΩΝ · WIPE ALL TEST DATA"}
        </Button>
      </Card>

      <Dialog open={hardResetOpen} onOpenChange={setHardResetOpen}>
        <DialogContent
          className="max-w-xl border-4 border-red-600 shadow-2xl shadow-red-300"
          data-testid="hard-reset-modal"
        >
          <DialogHeader>
            <DialogTitle
              className="text-xl font-black tracking-tight flex items-center gap-2 text-red-700"
              data-testid="hard-reset-modal-title"
            >
              <AlertTriangle className="h-6 w-6 animate-pulse" />
              ⚠️ ΟΛΙΚΗ ΔΙΑΓΡΑΦΗ ΔΕΔΟΜΕΝΩΝ / SYSTEM HARD RESET
            </DialogTitle>
          </DialogHeader>
          <div
            className="space-y-4 text-sm text-slate-800 leading-relaxed"
            data-testid="hard-reset-modal-body"
          >
            <p>
              <b>
                Είστε σίγουροι ότι θέλετε να μηδενιστούν ΟΛΑ τα δεδομένα σας;
              </b>{" "}
              Αυτή η ενέργεια θα σβήσει οριστικά όλα τα Τιμολόγια, Επιταγές,
              Cashflow, Z-Reports και Λογιστικά Τρίμηνα (TFA Form 4). Το σύστημα
              θα επανέλθει στο απόλυτο μηδέν (0.00 base).
            </p>
            <p>
              <b>Are you absolutely sure you want to wipe all ledger data?</b>{" "}
              This action will permanently erase all Invoices, Cheques, Cashflow
              entries, Z-Reports, and Quarterly Tax matrix logs. Data will
              revert to absolute 0.00 base status.
            </p>
            <div className="rounded-md bg-red-50 border border-red-300 px-3 py-2 text-[12px] text-red-900">
              <b>Tenant-scoped:</b> Σβήνονται ΜΟΝΟ τα δικά σας δεδομένα — άλλοι
              πελάτες / MASTER_JOHN παραμένουν ανέπαφοι.
            </div>
            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              <Button
                onClick={runHardReset}
                disabled={hardResetBusy}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-black uppercase tracking-widest h-12"
                data-testid="hard-reset-confirm"
              >
                {hardResetBusy
                  ? "Διαγραφή σε εξέλιξη…"
                  : "🔴 Ναι, Διαγραφή των Πάντων / Yes, Delete Everything"}
              </Button>
              <Button
                onClick={() => setHardResetOpen(false)}
                disabled={hardResetBusy}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase tracking-widest h-12"
                data-testid="hard-reset-cancel"
              >
                🟢 Ακύρωση / Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Iter 78 · EDI IMAP MAILBOX CREDENTIALS PANEL
// ────────────────────────────────────────────────────────────────────────
function EdiImapSettingsCard() {
  const [imapServer, setImapServer] = useState("");
  const [ediEmail, setEdiEmail] = useState("");
  const [mailboxPassword, setMailboxPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [connected, setConnected] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/edi/imap-settings")
      .then(({ data: d }) => {
        if (!alive) return;
        setImapServer(d.imap_server || "");
        setEdiEmail(d.edi_email || "");
        setConnected(!!d.connected);
        setUpdatedAt(d.updated_at || null);
      })
      .catch(() => { /* fail-soft */ });
    return () => { alive = false; };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data: d } = await api.post("/edi/imap-settings", {
        imap_server: imapServer,
        edi_email: ediEmail,
        mailbox_password: mailboxPassword,
      });
      if (d?.ok) {
        setConnected(!!d.connected);
        setUpdatedAt(d.updated_at || null);
        setMailboxPassword(""); // wipe the in-memory copy after save
        toast.success("📥 EDI IMAP saved · Οι ρυθμίσεις αποθηκεύτηκαν");
      } else {
        toast.error(`Save failed: ${d?.error || "unknown"}`);
      }
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      className="p-6 border-2 border-amber-300 bg-gradient-to-br from-amber-50/60 via-white to-amber-50/30"
      data-testid="settings-edi-imap-card"
    >
      <div className="flex items-center gap-2 mb-2">
        <div className="rounded-full bg-amber-500/15 p-2 ring-1 ring-amber-400/50">
          <SettingsIcon className="h-5 w-5 text-amber-700" />
        </div>
        <h2
          className="text-lg font-bold tracking-tight text-slate-900"
          data-testid="settings-edi-imap-title"
        >
          📥 ΡΥΘΜΙΣΕΙΣ EMAIL (EDI IMAP SETUP)
        </h2>
      </div>
      <p className="text-xs text-slate-500 mb-5 leading-relaxed">
        Configure the mailbox the EDI crawler watches for incoming
        electronic invoices · Ρυθμίστε το ταχυδρομείο που παρακολουθεί
        ο EDI crawler.
      </p>

      <div className="grid gap-4 sm:grid-cols-1">
        <div>
          <Label htmlFor="edi-imap-server" className="text-xs font-semibold uppercase tracking-wide text-slate-700">
            Incoming IMAP Server
          </Label>
          <Input
            id="edi-imap-server"
            data-testid="settings-edi-imap-server"
            value={imapServer}
            onChange={(e) => setImapServer(e.target.value)}
            placeholder="imap.gmail.com"
            className="mt-1 font-mono text-sm"
          />
        </div>

        <div>
          <Label htmlFor="edi-imap-email" className="text-xs font-semibold uppercase tracking-wide text-slate-700">
            EDI Email Address
          </Label>
          <Input
            id="edi-imap-email"
            data-testid="settings-edi-imap-email"
            type="email"
            value={ediEmail}
            onChange={(e) => setEdiEmail(e.target.value)}
            placeholder="invoices@kioskforce-erp.vip"
            className="mt-1 font-mono text-sm"
          />
        </div>

        <div>
          <Label htmlFor="edi-imap-password" className="text-xs font-semibold uppercase tracking-wide text-slate-700">
            Mailbox Password / App Token
          </Label>
          <div className="relative mt-1">
            <Input
              id="edi-imap-password"
              data-testid="settings-edi-imap-password"
              type={showPwd ? "text" : "password"}
              value={mailboxPassword}
              onChange={(e) => setMailboxPassword(e.target.value)}
              placeholder="••••••••••••••••"
              className="font-mono text-sm pr-20"
            />
            <button
              type="button"
              onClick={() => setShowPwd((v) => !v)}
              data-testid="settings-edi-imap-password-toggle"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] font-semibold text-slate-500 hover:text-slate-900 px-2 py-1 rounded hover:bg-slate-100"
            >
              {showPwd ? "HIDE" : "SHOW"}
            </button>
          </div>
        </div>
      </div>

      <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-amber-600 hover:bg-amber-700 text-white font-bold h-11 px-6 gap-2"
          data-testid="settings-edi-imap-save"
        >
          <Save className="h-4 w-4" />
          {saving ? "Saving…" : "💾 Save EDI Mailbox Credentials"}
        </Button>

        {connected && (
          <div
            data-testid="settings-edi-imap-status"
            className="inline-flex items-center gap-2 rounded-full bg-emerald-100 ring-1 ring-emerald-400 px-4 py-2 text-sm font-bold text-emerald-800 shadow-sm"
          >
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" />
            </span>
            🟢 Email Connected Successfully &amp; Crawler Active (24/7)
          </div>
        )}
      </div>

      {updatedAt && (
        <div className="text-[11px] text-slate-400 flex items-center gap-1 mt-3">
          <ShieldCheck className="h-3 w-3" />
          Last saved: {new Date(updatedAt).toLocaleString()}
        </div>
      )}
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Iter 79 · JCC / Viva Wallet API Connector + Bank Reconciliation Engine
// ────────────────────────────────────────────────────────────────────────
function JccVivaSettingsCard() {
  const [enabled, setEnabled] = useState(false);
  const [provider, setProvider] = useState("JCC");
  const [merchantId, setMerchantId] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [secretSet, setSecretSet] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/jcc-viva/settings")
      .then(({ data: d }) => {
        if (!alive) return;
        setEnabled(!!d.enabled);
        setProvider(d.provider || "JCC");
        setMerchantId(d.merchant_id || "");
        setSecretSet(!!d.api_secret_set);
        setUpdatedAt(d.updated_at || null);
      })
      .catch(() => { /* fail-soft */ });
    return () => { alive = false; };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data: d } = await api.post("/jcc-viva/settings", {
        enabled,
        provider,
        merchant_id: merchantId,
        api_secret: apiSecret,
      });
      if (d?.ok) {
        setSecretSet(!!d.api_secret_set);
        setUpdatedAt(d.updated_at || null);
        setApiSecret(""); // wipe the in-memory copy after save
        toast.success("💳 JCC / Viva config saved · Αποθηκεύτηκε");
      } else {
        toast.error(`Save failed: ${d?.error || "unknown"}`);
      }
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      className="p-6 border-2 border-indigo-300 bg-gradient-to-br from-indigo-50/60 via-white to-violet-50/40"
      data-testid="settings-jcc-viva-card"
    >
      <div className="flex items-center justify-between gap-4 mb-3">
        <div className="flex items-center gap-2">
          <div className="rounded-full bg-indigo-500/15 p-2 ring-1 ring-indigo-400/50">
            <SettingsIcon className="h-5 w-5 text-indigo-700" />
          </div>
          <h2
            className="text-lg font-bold tracking-tight text-slate-900"
            data-testid="settings-jcc-viva-title"
          >
            💳 JCC / VIVA WALLET API CONNECTOR
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <Label htmlFor="jcc-viva-toggle" className="text-xs font-semibold text-slate-700">
            Ενεργοποίηση Αυτόματης Visa (Optional)
          </Label>
          <Switch
            id="jcc-viva-toggle"
            data-testid="settings-jcc-viva-toggle"
            checked={enabled}
            onCheckedChange={setEnabled}
          />
        </div>
      </div>

      <p className="text-xs text-slate-500 mb-5 leading-relaxed">
        Direct API handshake with the Cyprus card gateway · Άμεση επικοινωνία
        με την τράπεζα για live reconciliation, terminal push, anti-fraud audit.
      </p>

      {enabled && (
        <div className="grid gap-4 sm:grid-cols-1" data-testid="settings-jcc-viva-fields">
          <div>
            <Label htmlFor="jcc-viva-provider" className="text-xs font-semibold uppercase tracking-wide text-slate-700">
              Payment Provider
            </Label>
            <select
              id="jcc-viva-provider"
              data-testid="settings-jcc-viva-provider"
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-mono shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="JCC">JCC</option>
              <option value="viva_wallet">Viva Wallet</option>
            </select>
          </div>

          <div>
            <Label htmlFor="jcc-viva-merchant" className="text-xs font-semibold uppercase tracking-wide text-slate-700">
              Merchant ID
            </Label>
            <Input
              id="jcc-viva-merchant"
              data-testid="settings-jcc-viva-merchant"
              value={merchantId}
              onChange={(e) => setMerchantId(e.target.value)}
              placeholder="e.g. MERCH-001 / 1234567890"
              className="mt-1 font-mono text-sm"
            />
          </div>

          <div>
            <Label htmlFor="jcc-viva-secret" className="text-xs font-semibold uppercase tracking-wide text-slate-700">
              Secure API Secret Token
            </Label>
            <div className="relative mt-1">
              <Input
                id="jcc-viva-secret"
                data-testid="settings-jcc-viva-secret"
                type={showSecret ? "text" : "password"}
                value={apiSecret}
                onChange={(e) => setApiSecret(e.target.value)}
                placeholder={secretSet ? "•••••••• (saved — type to replace)" : "Paste your API secret"}
                className="font-mono text-sm pr-20"
              />
              <button
                type="button"
                onClick={() => setShowSecret((v) => !v)}
                data-testid="settings-jcc-viva-secret-toggle"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] font-semibold text-slate-500 hover:text-slate-900 px-2 py-1 rounded hover:bg-slate-100"
              >
                {showSecret ? "HIDE" : "SHOW"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold h-11 px-6 gap-2"
          data-testid="settings-jcc-viva-save"
        >
          <Save className="h-4 w-4" />
          {saving ? "Saving…" : "💾 Save Card Gateway Config"}
        </Button>

        {enabled && secretSet && (
          <div
            data-testid="settings-jcc-viva-status"
            className="inline-flex items-center gap-2 rounded-full bg-indigo-100 ring-1 ring-indigo-400 px-4 py-2 text-sm font-bold text-indigo-800 shadow-sm"
          >
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-indigo-500" />
            </span>
            🟢 {provider === "viva_wallet" ? "Viva Wallet" : "JCC"} Gateway Linked · Push & Reconcile Active
          </div>
        )}
      </div>

      {updatedAt && (
        <div className="text-[11px] text-slate-400 flex items-center gap-1 mt-3">
          <ShieldCheck className="h-3 w-3" />
          Last saved: {new Date(updatedAt).toLocaleString()}
        </div>
      )}
    </Card>
  );
}


// ────────────────────────────────────────────────────────────────────────
// Iter 76 · System Updates archive + per-tenant feature toggles
// ────────────────────────────────────────────────────────────────────────
function SystemUpdatesCard() {
  const [catalog, setCatalog] = useState([]);
  const [flags, setFlags] = useState({});
  const [pending, setPending] = useState({});

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get("/system-updates/catalog"),
      api.get("/system-updates/flags"),
    ])
      .then(([cat, fl]) => {
        if (!alive) return;
        setCatalog(cat.data?.updates || []);
        setFlags(fl.data?.flags || {});
      })
      .catch(() => { /* fail-soft */ });
    return () => { alive = false; };
  }, []);

  const handleToggle = async (slug, next) => {
    setPending((p) => ({ ...p, [slug]: true }));
    setFlags((f) => ({ ...f, [slug]: next }));
    try {
      const { data: d } = await api.post(`/system-updates/flags/${slug}`, {
        enabled: next,
      });
      if (!d?.ok) {
        setFlags((f) => ({ ...f, [slug]: !next }));
        toast.error(`Toggle failed: ${d?.error || "unknown"}`);
      } else {
        toast.success(`${next ? "✅" : "⏸"} ${slug} ${next ? "ON" : "OFF"}`);
        window.dispatchEvent(new CustomEvent("kf:feature-flags-changed"));
      }
    } catch (e) {
      setFlags((f) => ({ ...f, [slug]: !next }));
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setPending((p) => {
        const c = { ...p }; delete c[slug]; return c;
      });
    }
  };

  return (
    <Card
      className="p-6 border-2 border-fuchsia-300 bg-gradient-to-br from-fuchsia-50/60 via-white to-violet-50/40"
      data-testid="settings-system-updates-card"
    >
      <div className="flex items-center gap-2 mb-2">
        <div className="rounded-full bg-fuchsia-500/15 p-2 ring-1 ring-fuchsia-400/50">
          <SettingsIcon className="h-5 w-5 text-fuchsia-700" />
        </div>
        <h2
          className="text-lg font-bold tracking-tight text-slate-900"
          data-testid="settings-system-updates-title"
        >
          🔄 SYSTEM UPDATES / ΑΝΑΒΑΘΜΙΣΕΙΣ
        </h2>
      </div>
      <p className="text-xs text-slate-500 mb-5 leading-relaxed">
        Permanent archive of every platform upgrade. Toggle individual
        features ON or OFF at any time · Μόνιμο ιστορικό όλων των
        αναβαθμίσεων.
      </p>

      <div
        className="space-y-2 max-h-[480px] overflow-y-auto pr-1"
        data-testid="settings-system-updates-list"
      >
        {catalog.length === 0 && (
          <div className="text-xs text-slate-400 italic py-4 text-center">
            Loading archive…
          </div>
        )}
        {catalog.map((entry) => {
          const on = !!flags[entry.slug];
          const busy = !!pending[entry.slug];
          return (
            <div
              key={entry.slug}
              data-testid={`update-row-${entry.slug}`}
              className={`group rounded-lg p-3 ring-1 transition-colors ${
                on ? "bg-emerald-50/60 ring-emerald-300" : "bg-slate-50/80 ring-slate-200"
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5 w-10 text-center">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                    iter
                  </div>
                  <div className="text-lg font-extrabold text-fuchsia-700 leading-none">
                    {entry.iter}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-slate-900 truncate">
                    {entry.title}
                  </div>
                  {entry.title_gr && (
                    <div className="text-[11px] text-slate-500 italic truncate">
                      {entry.title_gr}
                    </div>
                  )}
                  <p className="text-xs text-slate-600 mt-1 leading-snug">
                    {entry.summary}
                  </p>
                  <div className="text-[10px] text-slate-400 mt-1">
                    Released {entry.released_at}
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <Switch
                    data-testid={`update-toggle-${entry.slug}`}
                    checked={on}
                    disabled={busy}
                    onCheckedChange={(v) => handleToggle(entry.slug, v)}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-[10px] text-slate-400 mt-3 italic">
        ⓘ Toggling OFF hides the feature from your tenant scope but keeps
        the historical entry archived here. Updates never disappear.
      </p>
    </Card>
  );
}

